import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useParkingSearchStore } from '@shared/stores/useParkingSearchStore';

import { ROUTE_PATHS } from '@router/paths';
import { RouteName } from '@router/routeNames';

interface StartPackageReservationParams {
  parkingId: string;
  parkingSlug: string;
  priceRuleId: string;
}

/**
 * Entry point for buying a parking package. Records the chosen parking and price
 * rule, then hands off to the shared reservation flow — `ReservationFlowPage`
 * picks up the price rule (via `useEnsurePendingReservation`) and creates a
 * package reservation, after which the flow proceeds exactly like the standard
 * one (vehicle selection → summary → payment).
 */
export function useStartPackageReservation(): (params: StartPackageReservationParams) => void {
  const navigate = useNavigate();
  const setSelectedParking = useParkingSearchStore((state) => state.setSelectedParking);
  const setSelectedPriceRuleId = useParkingSearchStore((state) => state.setSelectedPriceRuleId);
  const setLastPriceEstimate = useParkingSearchStore((state) => state.setLastPriceEstimate);

  return useCallback(
    ({ parkingId, parkingSlug, priceRuleId }: StartPackageReservationParams) => {
      setSelectedParking(parkingId, parkingSlug);
      setSelectedPriceRuleId(priceRuleId);
      // A package has its own fixed price, so no standard estimate is compared on the summary.
      setLastPriceEstimate(undefined);
      navigate(ROUTE_PATHS[RouteName.RESERVATION_FLOW]);
    },
    [navigate, setSelectedParking, setSelectedPriceRuleId, setLastPriceEstimate]
  );
}
